
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Redirect, Link } from 'react-router-dom';
import { injectIntl, FormattedMessage } from 'react-intl';
import { getRemotedbUrl, i18nIntl, isNotEmpty } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';

import ActionButton, { BackButton } from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox';
import DisabledArea from '../builder/DisabledArea';
import EPPlatformVersion from '../builder/EPPlatformVersion';
import FloatingLayer from '../builder/FloatingLayer';
import { SuccessMessage } from '../builder/Message';
import SaveButton from '../builder/SaveButton';
import SectionTitle from '../builder/SectionTitle';

import SubscriptionSingle from './SubscriptionSingle';
import AppsIncludedTable from './AppsIncludedTable';
import BasicPlansComparisonTable from './BasicPlansComparisonTable';

import SectionHeader from '../ui_items/SectionHeader.js';
import ChargeBee, { UpgradeSubscription } from './ChargeBee.js';

const SelectPlan = ({ plan, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const disabled_plan = props?.disabled_plan ?? false;

	if(plan.admin_has_it !== 0 && !disabled_plan) {
		return (
			<ActionButton
				color="transparent"
				style={{ '--btn-bg-color':'var(--ep-green-color)' }}
				_class={{ other:'gui_lined_button blackBorder gui_withoutEvents' }}
			>Your current plan</ActionButton>
		)
	}

	const buttonClass = props?.buttonClass ?? '';

	return (
		<Link
			to={`/add/${plan.type}`}
			className={`${buttonClass} gui_flex_submit in_top`}
		>{i18n("Select plan")}</Link>
	)
};


export const priceCustomDaysPlan = (type, plan, currency) => {
	const _prizes = {
		//2: {'1day': { USD: 5, EUR: 5,}, '3day': { USD: 10, EUR: 10,}, Monthly: { USD: 29, EUR: 29,}},//NOt allowd pass day in Basic
		4: {'1day': { USD: 10, EUR: 10,}, '3day': { USD: 25, EUR: 25,}, Monthly: { USD: 199, EUR: 149,}},
		5: {'1day': { USD: 20, EUR: 20,}, '3day': { USD: 50, EUR: 50,}, Monthly: { USD: 399, EUR: 349,}},
	};
	const item_cost = _prizes?.[type]?.[plan]?.[currency] ?? null;
	if(!item_cost)return null;
	let item_cost_currency = (window.currency == "USD") ? `$${item_cost} USD` : `${item_cost}€`;
	return item_cost_currency;

}

export const chargebee_item = (plan_type, pay_mode, currency) => {
	let pay_mode_string = (pay_mode == 0) ? 'Monthly' : 'Yearly';
	if(pay_mode == '1day')pay_mode_string = '1-DAY';
	if(pay_mode == '3day')pay_mode_string = '3-DAY';

	let plan_type_string = "BASIC-PLAN";
	switch(plan_type) {
		case '2':
			plan_type_string = 'BASIC-PLAN';
			break;
		case '3':
			plan_type_string = 'BASIC-PRO-PLAN';
			break;
		case '4':
			plan_type_string = 'PREMIUM-PLAN';
			break;
		case '5':
			plan_type_string = 'WHITE-LABEL-PLAN';
			break;
		case '6':
			plan_type_string = 'CORPORATE-PLAN';
			break;
		case '7':
			plan_type_string = 'CORPORATE-PLUS-PLAN';
			break;
	}
	return `${plan_type}-${plan_type_string}-${currency}-${pay_mode_string}`;
};

const Subscriptions = (props) => {
	useCSS('comp/agent_cards.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ actual_subscriptions, setActualSubscriptions ] = useState(props?.actual_subscriptions ?? {});
	const [ just_cancelled, setJustCancelled ] = useState(props?.just_cancelled ?? false);
	const [ just_reactivated, setJustReactivated ] = useState(props?.just_reactivated ?? false);
	const [ just_upgraded_to_annual, setJustUpgradedToAnnual ] = useState(props?.just_upgraded_to_annual ?? false);
	const [ model, setModel ] = useState({ plan_periodicity:false });

	const getExtraInfoFromDB = (plan_type) => {
		let selected_plan = false;
		const force_plan = props.force_plan;

		if(force_plan) {
			selected_plan = props.plans.find((obj) => (obj.Plan.plan_type == plan_type && obj.Plan.description == force_plan));
		}

		if(props.group_id != null) {
			let gplan = props.plans.filter((obj) => (obj.Plan.plan_type == plan_type && obj.Plan.group_id == props.group_id));

			if(gplan.length > 0) {
				selected_plan = gplan[0];
			}
		}

		if(!selected_plan) {
			selected_plan = props.plans.find((obj) => (obj.Plan.plan_type == plan_type && obj.Plan.default == 1));
		}

		if(selected_plan == undefined) return false;

		const admin_has_it_active = ((actual_subscriptions.active != undefined) ? actual_subscriptions.active.find((obj) => (obj.Subscription.plan_id == selected_plan.Plan.id)) : false);
		const admin_has_it_pending = ((actual_subscriptions.pending != undefined) ? actual_subscriptions.pending.find((obj) => (obj.Subscription.plan_id == selected_plan.Plan.id)) : false);

		let admin_has_it = 0;
		if(admin_has_it_active) admin_has_it = 1;
		if(admin_has_it_pending) admin_has_it = 2;

		return {
			id:selected_plan.Plan.id,
			prices:{
				EUR:selected_plan.Plan.price_eur,
				USD:selected_plan.Plan.price_usd,
			},
			prices_annual:{
				EUR:selected_plan.Plan.price_annual_eur,
				USD:selected_plan.Plan.price_annual_usd,
			},
			admin_has_it:admin_has_it,
			plan_type:selected_plan.Plan.plan_type,
			promo_type:selected_plan.Plan.promo_type,
		};
	};

	const populatePlansStaticInfo = () => {
		const basic_plan = {
			type_id:0,
			plan_type:2,
			type:'basic',
			type_abbr:"BSC",
			type_label:"Basic",
			short_description:"Use this plan to run quick giveaways for users who interact with social media posts.",
			recommended_for:"Small to medium enterprises which run occasional quick giveaways on their social media.",
			features:[
				i18n("Includes unlimited giveaways on Facebook and Instagram for all the brands you work with."),
				i18n("Limited to 5000 participations per giveaway. Sponsored posts are not supported."),
				i18n("Includes the List giveaway App."),
				i18n("Includes basic giveaways with registration form."),
				i18n("Includes chat and email support."),
			],
			features_link:(window.is_spanish) ? 'suscripcion_basica' : 'basic_subscription',
			display_comparison_table:true,
			...getExtraInfoFromDB(2),
		};

		const basicPro_plan = {
			type_id:3,
			plan_type:3,
			type:'basicpro',
			type_abbr:"BSC",
			type_label:"Basic PRO",
			short_description:"A high-powered plan for quick social media giveaways. Unlimited entries, support for sponsored posts, and multi-network participation.",
			recommended_for:"Marketing professionals who organize their clients' social media and advertising campaigns.",
			features:[
				i18n("Includes all Basic apps, plus the Multi-Network giveaway app. Combine entries from different social networks in one giveaway."),
				i18n("Unlimited participations and support for sponsored posts on Facebook, Instagram, and Twitter."),
				i18n("Twitter giveaways using tweets up to 7 days old. Twitter giveaways using replies, and mentions + hashtags."),
				i18n("Includes chat and email support."),
			],
			features_link:(window.is_spanish) ? 'plan_basicpro' : 'basicpro_plan',
			...getExtraInfoFromDB(3),
		};

		const premium_plan = {
			type_id:1,
			plan_type:4,
			type:'premium',
			type_label:"Premium",
			short_description:"The best plan for collecting user data. Includes all promotion and contest types you need to achieve all your marketing objectives.",
			recommended_for:"Agencies and businesses which need a 360° platform for promotions and contests. It's reliable, flexible, and doesn't require programming at all.",
			features:[
				i18n("Includes all the apps and features listed in Basic PRO Plan."),
				i18n("Includes all Premium contest types: photos, videos, texts, and Pick Your Favorite."),
				i18n("Includes all methods for rewarding customer registration: giveaways, coupons, unique promotional codes, Instant Win, Refer a Friend, Redeem codes and Winning codes."),
				i18n("Includes all types of quiz: Knowledge quiz, Survey, Predictions and Personality quiz."),
				i18n("Includes chat and email support."),
			],
			features_link:(window.is_spanish) ? 'suscripcion_premium' : 'premium_subscription',
			...getExtraInfoFromDB(4),
		};

		const wl_plan = {
			type_id:2,
			plan_type:5,
			type:'wl',
			type_label:"White Label",
			short_description:"The most comprehensive Easypromos plan. Includes all applications, all features, and full customization.",
			recommended_for:"Large enterprises which run high-volume and international campaigns. Marketing agencies which need tools to automate and integrate with CRM and other systems.",
			features:[
				i18n("No mention of the Easypromos brand in your competition apps."),
				i18n("Global promotions with multilingual support."),
				i18n("Your own domain name with HTTPS support."),
				i18n("Access to the API and webhooks to synchronize participations with third-party systems."),
				i18n("Includes chat and email support."),
				i18n("Tools for developers to customize the web design, including HTML, CSS, and Javascript."),
			],
			features_link:(window.is_spanish) ? 'suscripcion_marcablanca' : 'whitelabel_subscription',
			...getExtraInfoFromDB(5),
		};

		return [
			basic_plan,
			basicPro_plan,
			premium_plan,
			wl_plan,
		];
	};

	const max_months_in_annual_plan = 10;
	const plans = populatePlansStaticInfo();
	const ceuta_melilla_canary = props.ceutamelillacanary;

	useEffect(() => {
		const subscriptions = [];
		actual_subscriptions.active.map((subs) => {
			const subs_ok = { ...subs };
			const pending_payments_in_subs = actual_subscriptions.pending.filter((obj) => (obj.Subscription.id === subs.Subscription.id));
			subs_ok.pending_payments = (pending_payments_in_subs) ? pending_payments_in_subs : [];
			subscriptions.push(subs_ok);
		});

		setActualSubscriptions({ ...actual_subscriptions, active:subscriptions });
	}, []);

	const change = (k, v) => {
		setModel({ ...model, [k]:v });
	};

	const doPayment = (plan_id, cb) => {
		const plan = plans.find((obj) => (obj.id === plan_id));

		const plan_options = calculateUpgradePrice(plan);

		if(plan_options.is_upgrade){
			window.location.href = `${window.panel_url}plans/upgrade_to_basic_pro/`;
		} else {
			let url_redirect = `${window.panel_url}payments/confirm/${plan.id}/`;
			if(!plan_options.is_upgrade) {
				url_redirect += (model.plan_periodicity) ? 'annual' : 'monthly';
			} else {
				url_redirect += (plan_options.is_annual) ? 'annual' : 'monthly';
			}
			url_redirect += (props.from_param) ? `?${props.from_param}` : '';

			if(cb != undefined) cb();
			window.location.href = getRemotedbUrl(url_redirect);
		}
	};

	const calculateUpgradePrice = (going_to_plan) => {
		const returns = {
			is_upgrade:false,
			plan_cost:going_to_plan.prices[window.currency],
			pending_to_pay:0,
			is_annual:false,
			months_to_pay:1,
			end_date:false,
			is_chargebee:false,
		};

		const upgrade_from = actual_subscriptions.active.find((obj) => (obj.Plan.promo_type === going_to_plan.promo_type && obj.Plan.plan_type < going_to_plan.plan_type));

		if(upgrade_from != undefined) {
			returns.is_upgrade = true;
			
			if(upgrade_from.Subscription.pay_mode == '1') {
				const months_since_last_payment = Math.floor(EPDateDiff(null, upgrade_from.Subscription.last_payment, 'months', true));
				
				returns.end_date = EPDateAdd(upgrade_from.Subscription.last_payment, 'years', 1);
				returns.is_annual = true;
				returns.months_to_pay = Math.min(max_months_in_annual_plan, 12 - months_since_last_payment);
				returns.plan_cost = max_months_in_annual_plan * going_to_plan.prices[window.currency];
			}
			returns.subscription = upgrade_from.Subscription;
			if(isNotEmpty(upgrade_from.Subscription, 'external_id')) returns.is_chargebee = true;
			returns.pending_to_pay = returns.months_to_pay * (going_to_plan.prices[window.currency] - upgrade_from.Plan[`price_${window.currency.toLowerCase()}`]);
		}

		return returns;
	};

	const getPlanFromActions = (subscription) => {
		const key_subs = actual_subscriptions.active.indexOf(subscription);
		if(key_subs >- 1) {
			const _actual_subscriptions = { ...actual_subscriptions };
			_actual_subscriptions.active[key_subs] = subscription;
			setActualSubscriptions(_actual_subscriptions);
		}
	};

	const route_basename = '/plans';

	const enable_plans_index = (actual_subscriptions.active.length > 0 || actual_subscriptions.pending.length > 0) ? true : false;

	const with_vat_msg = (props?.with_vat ?? false) ? ` + ${i18n("VAT")}` : '';

	let plan_populated = {};
	if(just_upgraded_to_annual && actual_subscriptions.active.length > 0) {
		const plan_description = (actual_subscriptions.active).find((obj) => (obj.Subscription.id == just_upgraded_to_annual));
		plan_populated = plans.find((obj) => (plan_description.Plan.plan_type == obj.plan_type));
	}

	let best_plan = false;
	if(actual_subscriptions.active.length > 0) {
		actual_subscriptions.active.map((plan) => {
			if(!best_plan || best_plan.plan_type < plan.Plan.plan_type) best_plan = plan.Plan;
		})
	}

	const active_subscriptions = [ ...actual_subscriptions.active ];

	return (
		<Router basename={route_basename} forceRefresh={false}>
			<section class="db_contents no_pages gui_grid">
				<Route path="/" exact={true} render={(route) => {
					const num_active_plans = (actual_subscriptions?.active ?? []).length;

					let num_cols = props?.num_cols ?? 4;
					const min_num_cols = props?.min_num_cols ?? 1;
					if(num_cols == 'auto') num_cols = Math.max(Math.min(num_active_plans, 4), min_num_cols);

					if(!enable_plans_index) {
						return (
							<Redirect to="/add" />
						)
					}

					return (
						<>
							<SectionHeader
								title={i18n("My plan")}
								total={<FormattedMessage
									id="{total} active plan(s)"
									values={{
										total:actual_subscriptions.active.length,
									}}
								/>}
								icon={{
									icon:'knk-subscription',
								}}
							>
								<ButtonCollection>
									<Link
										to="/add"
										className="alone_button gui_flex_submit knk-add-item gui_main_button blueButton gui_pos_rel create_promo_btn in_top"
									>{i18n("Add a new plan")}</Link>
								</ButtonCollection>
							</SectionHeader>

							<div class="agents_manager">
								<div class="cards_display" style={{ paddingBlockStart:'0', '--cards-gap-v':'var(--cards-gap-h)' }}>
									{(num_active_plans > 0) && (
										<div class="container" style={{gridTemplateColumns:`repeat(auto-fill, minmax(250px, 2fr))`}}>
											{active_subscriptions.map((subscription, i) => {
												const actual_plan = props.plans.find((obj) => (obj.Plan.id == subscription.Subscription.plan_id));
												if(actual_plan == undefined) return null;

												return (
													<SubscriptionSingle
														key={i}
														plans={plans}
														actual_plan={actual_plan}
														on_change_plan={getPlanFromActions}
														subscription={subscription}
													/>
												)
											})}
										</div>
									)}
								</div>
							</div>

							{(just_cancelled) && (
								<FloatingLayer backButton={() => setJustCancelled(false)}>
									<div class="without_actions">
										<SectionTitle
											_class={{
												icon:'knk-off',
												other:'greenIcon',
											}}
										>Your plan has been cancelled successfully!</SectionTitle>
									</div>
								</FloatingLayer>
							)}

							{(just_reactivated) && (
								<FloatingLayer backButton={() => setJustReactivated(false)}>
									<div class="without_actions">
										<SectionTitle
											_class={{
												icon:'knk-ok',
												other:'greenIcon',
											}}
										>Your plan has been reactivated successfully!</SectionTitle>
									</div>
								</FloatingLayer>
							)}

							{(just_upgraded_to_annual) && (
								<FloatingLayer backButton={() => setJustUpgradedToAnnual(false)} _class={{size:'small'}}>
									<div class="without_actions">
										<SectionTitle
											_class={{
												icon:'knk-on',
												other:'greenIcon',
											}}
											autotranslate={false}
										>
											<FormattedMessage
												id="Your {plan} plan monthly has been upgraded to annual plan successfully!"
												values={{
													plan:i18n(plan_populated.type_label),
												}}
											/>
										</SectionTitle>
									</div>
								</FloatingLayer>
							)}
						</>
					)
				}} />

				<Route path="/add" exact={true} render={(route) => {
					const currency_pdf = (window.currency == 'EUR') ? 'euros' : 'usd';
					const generic_pdf_url = `${window.web_base_url}docs/`;

					return (
						<div class="promo_type_selector plan_type_selector gui_fullscreen_padded_section main_bg" style={{paddingTop:'25px'}}>
							<header class="gui_stepped_section_header">
								<SectionTitle autotranslate={false}>
									<span class="gui_icon promo_type_icon knk-subscription whiteIcon">
										<span class="bg" style={{backgroundColor:'#000000'}} />
									</span>

									<nav class="gui_breadcrumbs">
										<span>{i18n("Choose your plan")}</span>
									</nav>
								</SectionTitle>

								{(enable_plans_index) && (
									<ButtonCollection>
										<Link
											to="/"
											className="alone_button gui_flex_submit gui_secondary_button blueButton gui_pos_rel applications_btn in_top knk-go-prev"
										>{i18n("Go back")}</Link>
									</ButtonCollection>
								)}
							</header>

							<ul class="gui_sided_elements">
								{plans.map((plan, i) => {
									const disabled_plan = best_plan && best_plan.plan_type>plan.plan_type ? true : false;
									let li_classes = ['ep_version_block', `is_${plan.type}`];
									if(plan.admin_has_it == 0) li_classes.push('gui_pos_rel');

									let buttonClass;
									let colorVar;
									switch(plan.type) {
										case 'basicpro':
											buttonClass = `basicProButton`;
											colorVar = `--ep-basic-pro-color`;
											break;

										case 'premiumpro':
											buttonClass = `premiumProButton`;
											colorVar = `--ep-premium-pro-color`;
											break;

										default:
											buttonClass = `${plan.type}Button`;
											colorVar = `--ep-${plan.type}-color`;
											break;
									}

									if(disabled_plan) {
										buttonClass = `lightgrayButton`;
										colorVar = `--main-text-color`;
									}

									return (
										<li key={i} class={li_classes.join(" ")} style={{'--main-color':`var(${colorVar})`}}>
											<DisabledArea disabled={disabled_plan} message="You currently have a better plan">
												<div class="content">
													<EPPlatformVersion version={plan.type} />

													<div class="padded_children">
														<div class="ep_version">
															<div class="view_monthly price">
																<div class="price_module in_top">
																	<span>[ <span><strong>{plan.prices[window.currency]}</strong> {window.currency}/{i18n("month")}</span> | <span class="greenColor"><strong>{max_months_in_annual_plan * plan.prices[window.currency]}</strong> {window.currency}/{i18n("year")}</span> ]</span>

																	<p class="greenColor">({i18n("Save 2 months with annual payment")})</p>
																</div>
															</div>

															<div class="action_block without_info not_in_top gui_padded_in_bottom">
																<SelectPlan
																	plan={plan}
																	disabled_plan={disabled_plan}
																	buttonClass={buttonClass}
																	intl={props.intl}
																/>
															</div>

															<AppsIncludedTable _class={{ other:'not_in_top' }} />
														</div>

														<div class="is_recommended_for">
															<strong>{i18n("Recommended for")}</strong>

															<p
																dangerouslySetInnerHTML={{ __html:i18n(plan.recommended_for) }}
															/>
														</div>

														<p
															class="highlighted_section"
															dangerouslySetInnerHTML={{ __html:i18n(plan.short_description) }}
														/>

														<ul class="gui_checked_list">
															{plan.features.map((feature, k) => {
																return (
																	<li key={k} dangerouslySetInnerHTML={{ __html:feature }} />
																)
															})}

															{(plan?.display_comparison_table ?? false) && (
																<li>
																	<BasicPlansComparisonTable />
																</li>
															)}

															{(plan.features_link) && (
																<li>
																	<ActionLink
																		_href={`${generic_pdf_url}${plan.features_link}_${currency_pdf}.pdf`}
																		_target="_blank"
																	>Plan full features (PDF)</ActionLink>
																</li>
															)}
														</ul>
													</div>
												</div>

												<div class="padded_children">
													<div class="action_block without_info">
														<SelectPlan
															plan={plan}
															disabled_plan={disabled_plan}
															buttonClass={buttonClass}
															intl={props.intl}
														/>
													</div>
												</div>
											</DisabledArea>
										</li>
									)
								})}
							</ul>
						</div>
					)
				}} />

				<Route exact={true} path="/add/:type/:description?" render={(route) => {
					const type = route.match.params.type;
					const plan = plans.find((obj) => (obj.type === type));

					const already_with_this_plan = (actual_subscriptions.active.find((obj) => (obj.Plan.id == plan.id)) != undefined || actual_subscriptions.pending.find((obj) => (obj.Plan.id == plan.id)) != undefined) ? true : false;

					const new_plans_props = calculateUpgradePrice(plan);
					const item = chargebee_item(plan.plan_type, model.plan_periodicity, window.currency);

					return (
						((already_with_this_plan) ? (
							<Redirect to="/" />
						) : (
							<div class="promo_type_selector plan_type_selector gui_fullscreen_padded_section main_bg" style={{paddingTop:'25px'}}>
								<header class="gui_stepped_section_header">
									<SectionTitle autotranslate={false}>
										<span class="gui_icon promo_type_icon knk-subscription whiteIcon">
											<span class="bg" style={{backgroundColor:'#000000'}} />
										</span>

										<nav class="gui_breadcrumbs">
											<span>{i18n("Choose your plan")}</span>

											<span class="breadcrumb_separator knk-arrow-r-big" />

											<strong>{i18n(`${plan.type_label} Plan`)}</strong>
										</nav>
									</SectionTitle>

									<ButtonCollection>
										{(!props.back_url) ? (
											<Link
												to="/add"
												className="alone_button gui_flex_submit gui_secondary_button blueButton gui_pos_rel applications_btn in_top knk-go-prev"
											>{i18n("Go back")}</Link>
										) : (
											<BackButton
												_href={props.back_url}
											/>
										)}
									</ButtonCollection>
								</header>

								<div class="plan_selector_resume">
									<aside class={'selected_plan_review'+((new_plans_props.is_upgrade) ? ' is_upgrade' : '')}>
										<div>
											<EPPlatformVersion
												version={plan.type}
											/>

											<p
												dangerouslySetInnerHTML={{ __html:i18n(plan.short_description) }}
												style={{
													padding:'0 2em 1.8em',
													textAlign:'center',
												}}
											/>
										</div>

										{(plan.type == 'basic') && (
											<ul class="gui_checked_list gui_custom_icons gui_is_not_padded gui_not_in_top_small">
												<li class="gui_text_align_start with_pro_mark">
													<span class="is_pro_mark">{i18n("PRO")}</span>

													<FormattedMessage id="Need more powerful features? Upgrade to a {better_plan}." values={{better_plan:i18n("Basic PRO Plan")}} /> <BasicPlansComparisonTable />.
												</li>
											</ul>
										)}
									</aside>

									<div style={{ flexGrow:'1' }}>
										<div class="plan_hiring_conditions">
											<div>
												<h3>{i18n("Contract details")}:</h3>
												{(new_plans_props.is_upgrade && new_plans_props.pending_to_pay && new_plans_props.is_annual && new_plans_props.months_to_pay <= max_months_in_annual_plan && new_plans_props.end_date && !new_plans_props.is_chargebee) && (
													<SuccessMessage
														_class={{ other:'in_bottom gui_not_in_top_small' }}
													>
														<p><FormattedMessage id="The price to upgrade your current plan to a {plan}: {price}." values={{plan:i18n(`${plan.type_label} Plan`), price:<strong>{new_plans_props.pending_to_pay} {window.currency}{with_vat_msg}</strong>}} /></p>

														<p><FormattedMessage id="The upgrade price is calculated based on the months left on your current plan, with a cost of {monthly_price}." values={{monthly_price:<strong>{new_plans_props.pending_to_pay / new_plans_props.months_to_pay} {window.currency}{with_vat_msg} / {i18n("month")}</strong>}} /></p>

														<p><FormattedMessage id="The renewal date will not change. If you keep on with the upgrade, the {plan} rate will be applied on the following month." values={{plan:i18n(`${plan.type_label} Plan`)}} /></p>
													</SuccessMessage>
												)}

												{(new_plans_props.is_upgrade && new_plans_props.pending_to_pay && !new_plans_props.is_annual && !new_plans_props.is_chargebee) && (
													<SuccessMessage
														_class={{ other:'in_bottom gui_not_in_top_small' }}
													>
														<p><FormattedMessage id="The price to upgrade your current plan to a {plan}: {price}." values={{plan:i18n(`${plan.type_label} Plan`), price:<strong>{new_plans_props.pending_to_pay} {window.currency}{with_vat_msg}</strong>}} /></p>

														<p><FormattedMessage id="The renewal date will not change. If you keep on with the upgrade, the {plan} rate will be applied on the following month." values={{plan:i18n(`${plan.type_label} Plan`)}} /></p>
													</SuccessMessage>
												)}

												{(new_plans_props.is_upgrade && new_plans_props.is_chargebee) && (
													<SuccessMessage _class={{ other:'in_bottom gui_not_in_top_small' }}>
														<p><FormattedMessage id="Continue to see the prorated price for your plan upgrade"/></p>
													</SuccessMessage>
												)}

												<ul class="gui_checked_list gui_custom_icons">
													<li class="knk-arrow-r-big">
														<FormattedMessage id="{plan}: {price}." values={{plan:i18n(`${plan.type_label} Plan`), price:<strong>{new_plans_props.plan_cost} {window.currency}{with_vat_msg}{(!new_plans_props.is_annual) ? ' / '+i18n("month") : ''}</strong>}} />
													</li>

													{!new_plans_props.is_annual && (
														<li class="knk-arrow-r-big">{i18n("Automatic renewal every 30 days.")}</li>
													)}

													{!new_plans_props.is_annual && (
														<li class="knk-arrow-r-big">{i18n("Automatic renewal can be cancelled at any time.")}</li>
													)}
												</ul>
											</div>

											<div>
												<ul class="gui_checked_list gui_custom_icons">
													<li class="knk-arrow-r-big">{i18n("Includes unlimited giveaways and promotions.")}</li>

													<li class="knk-arrow-r-big">{i18n("Connect all the brands and social media accounts that you manage.")}</li>
												</ul>
											</div>
										</div>

										{(!new_plans_props.is_upgrade) && (
											<Checkbox
												name="plan_periodicity"
												model={model}
												change={(k, v) => change(k, v)}
												label_props={{
													_extra_component:<strong>{plan.prices_annual[window.currency]} {window.currency}{with_vat_msg}</strong>,
												}}
											>I want to save 2 months and sign up for the annual plan</Checkbox>
										)}

										<ButtonCollection _class={{ other:'not_in_top' }}>
											{(!new_plans_props.is_upgrade && !ceuta_melilla_canary) && (
												<ChargeBee
													account_id={window.account_id}
													item_id={item}
													type='plan'
													onSuccess={(hosted_page) => {
														setCookie('processing_redirect', '/plans', 1, '.easypromosapp.com');
														window.location.href = `/payments/processing/${hosted_page.hosted_page_id}`;
													}}
												>Continue</ChargeBee>
											)}

											{(ceuta_melilla_canary || (new_plans_props.is_upgrade && !new_plans_props.is_chargebee)) && (
												<SaveButton
													onClick={(e, cb) => doPayment(plan.id, cb)}
												>Continue</SaveButton>
											)}

											{(new_plans_props.is_upgrade && new_plans_props.is_chargebee) && (
												<UpgradeSubscription
													subscription_id={new_plans_props.subscription.id}
													itemPriceId={chargebee_item('3', new_plans_props.subscription.pay_mode, window.currency)}
													onSuccess={() => {
														window.location.href = `/payments/processing`;
													}}
													autotranslate={false}
												>
													<FormattedMessage
														id="Upgrade to {type} Plan"
														values={{ type:"Basic Pro" }}
													/>
												</UpgradeSubscription>
											)}
										</ButtonCollection>
									</div>
								</div>
							</div>
						))
					)
				}} />
			</section>
		</Router>
	)
};

export default injectIntl(Subscriptions);